import { Injectable } from '@angular/core';
import { GenericDtoAssemblerService, Guid } from '@ebeihilfe/core';

import { AntragMetaDataDto } from '../dto/antrag-meta-data';
import { FormValueDto } from '../dto/form-value.dto';
import { AntragMetaData, FormResult, FormValue } from '../model';

// The survey→model mapper stores the time-only field (WI 143285) under the internal `time` type, but
// the backend's metadata deserializer keys on the wire discriminator `time-only`
// (`TimeOnlyFieldDeserializer.CanDeserialize`). Map the `$type` at this model↔wire boundary so the
// internal representation stays consistent while the serialized DTO matches the backend contract.
const MODEL_TO_WIRE_TYPE: Readonly<Record<string, string>> = { time: 'time-only' };
const WIRE_TO_MODEL_TYPE: Readonly<Record<string, string>> = { 'time-only': 'time' };

@Injectable({
  providedIn: 'root',
})
export class AntragMetaDataDtoAssemblerService extends GenericDtoAssemblerService<AntragMetaData, AntragMetaDataDto> {
  public toDto(antragMetadata: AntragMetaData): AntragMetaDataDto {
    return new AntragMetaDataDto(
      antragMetadata.formResults.map((formResults) => ({
        templateName: formResults.templateName,
        formId: formResults.formId.toString(),
        isValid: formResults.isValid,
        createdAt: formResults.createdAt,
        isMandatory: formResults.isMandatory,
        results: formResults.results.filter((element) => element.value != undefined).map(this.createFormValueDto),
      })),
    );
  }

  public fromDto(antragMetaDataDto: AntragMetaDataDto): AntragMetaData {
    return new AntragMetaData({
      formResults: antragMetaDataDto.formResults.map(
        (formResultsDto) =>
          new FormResult({
            templateName: formResultsDto.templateName,
            formId: Guid.create(formResultsDto.formId),
            createdAt: formResultsDto.createdAt,
            isValid: formResultsDto.isValid,
            isMandatory: formResultsDto.isMandatory,
            results: formResultsDto.results.map(this.createFormValue),
          }),
      ),
    });
  }

  private createFormValueDto(formValue: FormValue): FormValueDto {
    const wireType = MODEL_TO_WIRE_TYPE[formValue.$type] ?? formValue.$type;
    return new FormValueDto(wireType, formValue.name, formValue.value, formValue.parent);
  }

  private createFormValue(formValueDto: FormValueDto): FormValue {
    const modelType = WIRE_TO_MODEL_TYPE[formValueDto.$type] ?? formValueDto.$type;
    return FormValue.create(modelType, formValueDto.name, formValueDto.value, formValueDto.parent);
  }
}
